export type CommunicationStatusKey =
  | 'received'
  | 'pending'
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'failed'
  | 'other';

export type CommunicationStatusFilter = Exclude<CommunicationStatusKey, 'other'>;

/**
 * Raw `communications.status` values behind each display bucket. The badge mapping and
 * the server-side status filter both read this, so they cannot drift apart.
 *
 * Inbound texts are stored as `received` (twilio-webhook). Twilio's in-flight states rank
 * before `sent` in _shared/sms-delivery.ts, so they read as pending. Missing and
 * unrecognized values are `other`, never pending, and no filter selects them.
 */
export const STATUS_GROUPS: Readonly<Record<CommunicationStatusFilter, readonly string[]>> = {
  received: ['received'],
  pending: ['pending', 'accepted', 'queued', 'sending'],
  sent: ['sent'],
  delivered: ['delivered'],
  opened: ['opened'],
  clicked: ['clicked'],
  failed: ['failed']
};

const KEY_BY_STATUS = new Map<string, CommunicationStatusFilter>(
  (Object.entries(STATUS_GROUPS) as [CommunicationStatusFilter, readonly string[]][])
    .flatMap(([key, statuses]) => statuses.map((status) => [status, key] as const))
);

/** Display bucket for a raw `communications.status` value. */
export function communicationStatusKey(status: string | null | undefined): CommunicationStatusKey {
  return (status && KEY_BY_STATUS.get(status)) || 'other';
}

export function isCommunicationStatusFilter(value: string): value is CommunicationStatusFilter {
  return Object.prototype.hasOwnProperty.call(STATUS_GROUPS, value);
}

/** Raw statuses a status filter selects on the server. */
export function communicationStatusesFor(key: CommunicationStatusFilter): readonly string[] {
  return STATUS_GROUPS[key];
}
